<template>
    <section
        class="mx-auto flex h-full min-h-[500px] w-full max-w-4xl flex-col items-center justify-center gap-y-0 overflow-hidden px-6 py-4 text-white lg:py-6 xl:max-w-5xl"
    >
        <div
            class="flex min-h-0 w-full flex-1 items-center justify-center"
            style="max-height: 450px"
        >
            <img
                v-if="slide.image?.url"
                :src="slide.image.url"
                :alt="slide.image.alt || ''"
                loading="eager"
                class="mx-auto block h-full max-h-full w-auto object-contain"
            />
        </div>
        <div class="w-full flex-shrink-0 pt-3 text-center">
            <h2
                class="text-pretty text-xl font-bold lg:text-2xl"
                tabindex="-1"
                v-html="slide.questionText"
            ></h2>
            <p
                v-if="slide.hint"
                v-html="slide.hint"
                class="mt-1 text-pretty text-xs lg:text-base"
            ></p>
            <div class="mt-3 grid gap-3 md:mt-6 md:grid-cols-2 lg:gap-4">
                <button
                    v-for="(choice, i) in slide.choices"
                    :key="choice.label"
                    type="button"
                    :class="choiceClass(i)"
                    @click="select(i)"
                    :disabled="answered"
                >
                    <span
                        class="relative flex w-full items-center justify-center"
                    >
                        <span class="text-center">
                            {{ choice.label }}
                        </span>
                        <span
                            v-if="answered"
                            class="absolute right-0 inline-flex items-center"
                        >
                            <IconCheck
                                v-if="isCorrectChoice(i)"
                                class="choice-icon size-6 text-[#75A700] lg:size-8"
                            />
                            <IconX
                                v-else-if="isSelected(i)"
                                class="choice-icon size-6 text-red-700/90 lg:size-8"
                            />
                        </span>
                    </span>
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import type { QuestionSlide as QuestionSlideType } from "../types";
import { useQuizStore } from "../stores/quizStore";
import { useFormieStore } from "../stores/formieStore";
import { useAnalyticsStore } from "../stores/analyticsStore";
import IconCheck from "../components/icons/IconCheck.vue";
import IconX from "../components/icons/IconX.vue";

const props = defineProps<{ slide: QuestionSlideType }>();

const router = useRouter();
const quiz = useQuizStore();
const formie = useFormieStore();
const analytics = useAnalyticsStore();
const answerEntry = computed(() => quiz.getAnswer(props.slide.questionId));
const answered = computed(() => !!answerEntry.value);
const selectedIndex = computed(() => answerEntry.value?.selectedIndex ?? -1);

function isSelected(i: number) {
    return i === selectedIndex.value;
}

function isCorrectChoice(i: number) {
    return !!props.slide.choices[i]?.isCorrect;
}

function choiceClass(i: number) {
    const base =
        "choice-btn  rounded-full bg-white px-10 py-2.5 text-center font-semibold text-blue duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky active:bg-sky md:w-auto disabled:text-body lg:text-xl lg:py-4 ";

    if (!answered.value) {
        return (
            base +
            " active:bg-transparent active:text-white active:ring-2 active:ring-white can-hover:hover:bg-transparent can-hover:hover:text-white can-hover:hover:ring-2 can-hover:hover:ring-white"
        );
    }

    const selected = isSelected(i);
    const correct = isCorrectChoice(i);

    if (correct) {
        return (
            base +
            " disabled:text-black/80 disabled:bg-yellow disabled:ring-2 disabled:ring-yellow"
        );
    }

    if (selected && !correct) {
        return (
            base +
            " disabled:text-black/70 disabled:bg-yellow/50 disabled:ring-2 disabled:ring-yellow/50"
        );
    }

    return (
        base +
        " disabled:text-sky disabled:ring-2 disabled:ring-sky disabled:bg-transparent"
    );
}

async function select(i: number) {
    if (!props.slide) return;
    if (answered.value) return;

    const isCorrect = !!props.slide.choices[i].isCorrect;
    const label = props.slide.choices[i].label;
    quiz.answerQuestion(props.slide, i);

    await new Promise((resolve) => setTimeout(resolve, 100));

    analytics.fire("quiz_answer", {
        questionId: props.slide.questionId,
        correct: isCorrect,
        choice: label,
        score: quiz.score,
        totalQuestions: quiz.totalQuestions,
    });

    await formie.createOrUpdateSubmission({
        currentQuestion: props.slide.questionId,
        score: quiz.score,
        totalQuestions: quiz.totalQuestions,
        answersJson: JSON.stringify(quiz.answers),
    });

    setTimeout(() => {
        router.push({
            name: "feedback",
            params: { id: props.slide.questionId },
        });
    }, 250);
}

// const showNext = computed(
//     () =>
//         quiz.shouldShowRevisitControls(props.slide.questionId) &&
//         quiz.getNextQuestionId(props.slide.questionId),
// );

// function goNext() {
//     const nextId = quiz.getNextQuestionId(props.slide.questionId);

//     if (!nextId) {
//         router.push({ name: "score" });
//         return;
//     }

//     router.push({ name: "question", params: { id: nextId } });
// }
</script>
<style>
.choice-btn {
    -webkit-tap-highlight-color: transparent;
}
.choice-icon {
    animation: pop-in 160ms ease-out;
}
@keyframes pop-in {
    0% {
        transform: scale(0.6);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>
